<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Copy } from 'lucide-vue-next'
import { accountsApi, ApiError, employeesApi, type CreateMechanicResponse } from '@ck/api-client'
import { CkAlert, CkAvatar, CkButton, CkField } from '@ck/ui'
import { formatDate } from '@ck/domain'

const queryClient = useQueryClient()
const roster = useQuery({ queryKey: ['employees-roster'], queryFn: () => employeesApi.roster() })

const form = reactive({ displayName: '', email: '' })
const errors = ref<{ displayName?: string[]; email?: string[] }>({})
const conflict = ref(false)
const created = ref<CreateMechanicResponse | null>(null)
const copied = ref(false)
const showForm = ref(false)

const mutation = useMutation({
  mutationFn: () => accountsApi.createMechanic({ displayName: form.displayName.trim(), email: form.email.trim() }),
  onSuccess: (response) => {
    created.value = response
    copied.value = false
    form.displayName = ''
    form.email = ''
    queryClient.invalidateQueries({ queryKey: ['employees-roster'] })
  },
  onError: (error: unknown) => {
    if (error instanceof ApiError) {
      conflict.value = error.problem.status === 409
      if (error.problem.errors) errors.value = error.problem.errors
    }
  }
})

function submit() {
  conflict.value = false
  created.value = null
  errors.value = {}
  if (form.displayName.trim().length < 3) errors.value.displayName = ['Nome deve ter ao menos 3 caracteres.']
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errors.value.email = ['E-mail inválido.']
  if (Object.keys(errors.value).length > 0) return
  mutation.mutate()
}

async function copyPassword() {
  if (!created.value) return
  await navigator.clipboard.writeText(created.value.temporaryPassword)
  copied.value = true
}

</script>

<template>
  <div>
    <div class="page-head">
      <div><h1>Equipe</h1><p>Restauradores e atribuições</p></div>
      <CkButton variant="secondary" @click="showForm = !showForm">{{ showForm ? 'Fechar' : 'Adicionar mecânico' }}</CkButton>
    </div>

    <section v-if="showForm" class="panel form-panel">
      <h2>Adicionar mecânico</h2>
      <CkAlert v-if="created" tone="success">
        Conta criada para <strong>{{ created.displayName }}</strong>. Senha temporária — anote agora, ela não será exibida novamente:
        <div class="temp-password">
          <code class="ck-code">{{ created.temporaryPassword }}</code>
          <button type="button" class="copy" @click="copyPassword"><Copy :size="14" />{{ copied ? 'Copiada' : 'Copiar' }}</button>
        </div>
        O mecânico deverá trocá-la no primeiro acesso ao Checklist.
      </CkAlert>
      <CkAlert v-else-if="conflict" tone="error">Já existe uma conta com este e-mail.</CkAlert>
      <CkAlert v-else-if="mutation.isError.value" tone="error">Não foi possível criar o mecânico. Tente novamente.</CkAlert>

      <form class="mechanic-form" @submit.prevent="submit">
        <CkField v-model="form.displayName" label="Nome completo" autocomplete="name" required :error="errors.displayName?.[0]" :disabled="mutation.isPending.value" />
        <CkField v-model="form.email" label="E-mail" type="email" autocomplete="email" required :error="errors.email?.[0]" :disabled="mutation.isPending.value" />
        <CkButton type="submit" :disabled="mutation.isPending.value">{{ mutation.isPending.value ? 'Criando…' : 'Criar mecânico' }}</CkButton>
      </form>
    </section>

    <CkAlert v-if="roster.isError.value" tone="error">Não foi possível carregar a equipe.</CkAlert>
    <div v-else class="roster">
      <article v-for="employee in roster.data.value ?? []" :key="employee.id" class="card">
        <div class="head">
          <CkAvatar :name="employee.displayName" :size="44"/>
          <div><strong>{{ employee.displayName }}</strong><span>{{ employee.role }} · na equipe desde {{ formatDate(employee.memberSince) }}</span></div>
        </div>
        <div class="assigned">
          <div class="label">Atribuído a</div>
          <span v-if="employee.assignedVehicles.length === 0" class="available">Disponível</span>
          <div v-else class="chips">
            <span v-for="vehicle in employee.assignedVehicles" :key="vehicle.workOrderId" class="chip">{{ vehicle.vehicleBrand }} {{ vehicle.vehicleModel }}</span>
          </div>
        </div>
      </article>
      <p v-if="roster.data.value?.length === 0" class="empty">Nenhum colaborador cadastrado.</p>
    </div>
  </div>
</template>

<style scoped>
.page-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
.page-head h1 { margin: 0; font-family: var(--font-display); font-size: 26px; font-weight: 800; }
.page-head p { margin: 4px 0 0; color: var(--color-text-faint); font-size: 13px; }
.panel { margin-bottom: 20px; padding: 20px; border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.panel h2 { margin: 0 0 16px; font-size: 18px; }
.mechanic-form { display: grid; gap: 4px; max-width: 420px; }
.temp-password { display: flex; align-items: center; gap: 10px; margin: 10px 0; padding: 10px 14px; border-radius: var(--radius-md); background: rgba(120,100,80,.12); }
.temp-password code { font-size: 15px; letter-spacing: .04em; }
.copy { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: 4px 10px; background: transparent; color: inherit; cursor: pointer; font-size: 12px; }
.copy:hover { border-color: var(--color-accent); color: var(--color-accent); }
.roster { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.card { padding: 18px 20px; border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.head { display: flex; align-items: center; gap: 13px; }
.head strong { display: block; font-family: var(--font-display); font-weight: 700; font-size: 15.5px; }
.head span { display: block; color: var(--color-text-muted); font-size: 12.5px; margin-top: 1px; }
.assigned { margin-top: 14px; padding-top: 13px; border-top: 1px solid var(--color-border); }
.label { font-size: 10.5px; font-weight: 700; letter-spacing: var(--ls-wide); color: var(--color-text-faint); margin-bottom: 8px; }
.available { font-family: var(--font-serif); font-style: italic; color: var(--color-text-faint); font-size: 13px; }
.chips { display: flex; flex-wrap: wrap; gap: 7px; }
.chip { background: var(--color-surface-muted); border: 1px solid var(--color-border); color: var(--color-text-muted); font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 99px; }
.empty { color: var(--color-text-muted); font-size: 13px; }
@media (max-width: 900px) { .roster { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .roster { grid-template-columns: 1fr; } .page-head { flex-direction: column; gap: 10px; } }
</style>
